import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  headerHeight,
  headerHeightMedium,
  minMedium,
  shade950,
} from "@songtradr-one/components-frontend";
import {
  AppModuleKey,
  appModulesList,
  routes,
} from "@songtradr-one/shared-common";
import { useAppContext } from "@songtradr-one/shared-frontend";
import { staticText } from "../../shared/statics/static-text";

// TEXT
export const text = staticText.appHeader;

// STYLES
const transparentShade950 = `color-mix(in srgb, ${shade950} 0%, transparent)`;
const navGradient = `linear-gradient(180deg, ${shade950} 0%, ${transparentShade950} 100%)`;

export const NavBar = styled.div<{ $dropdownOpen?: boolean }>`
  display: grid;
  position: relative;
  width: 100%;
  z-index: 4;
  top: 0;
  left: 0;
  min-height: ${headerHeight};
  align-items: center;
  justify-content: space-between;
  padding: 0 1.5rem;
  grid-template-columns: minmax(0, 1fr) min-content minmax(0, 1fr);
  background: ${({ $dropdownOpen: open }) => (open ? shade950 : navGradient)};

  @media ${minMedium} {
    min-height: ${headerHeightMedium};
  }
`;

export const NavBarLinks = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: clamp(1rem, 3vw, 3rem);
  align-items: center;
  padding-right: 1rem;
  min-width: 0;
  /* Pinned so Branding stays centered in the middle column by elimination,
   * regardless of whether this is empty at narrower sizes. */
  grid-column: 1;
`;

export const NavBarTools = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  grid-column: 3;
`;

export const MobileGridIconSpacer = styled.div`
  width: 1.5rem;
`;

// DATA

// TYPES

// FUNCTIONS
export const useAppHeader = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasAllPermissions, hasAnyPermission } = useAppContext();
  const [userMenuOpen, setShowUserMenu] = useState(false);
  const [controlCenterOpen, setControlCenterOpen] = useState(false);

  const isHome = location.pathname === routes.portal.base;

  const activeModule = isHome
    ? appModulesList.find((m) => m.key === AppModuleKey.PORTAL)
    : appModulesList.find((m) =>
        m.baseRoute !== routes.portal.base &&
        m.subModules &&
        m.subModules.length > 0 &&
        m.baseRoute
          ? location.pathname === m.baseRoute ||
            location.pathname.startsWith(`${m.baseRoute}/`)
          : false,
      );

  const subModules = (activeModule?.subModules ?? [])
    .filter((subModule) =>
      subModule.matchRoute
        ? location.pathname.startsWith(subModule.matchRoute)
        : true,
    )
    .filter((subModule) =>
      subModule.permissions
        ? (subModule.match ?? "AllPermissions") === "AllPermissions"
          ? hasAllPermissions(subModule.permissions)
          : hasAnyPermission(subModule.permissions)
        : true,
    );

  const toggleUserMenu = () => {
    setShowUserMenu(!userMenuOpen);
  };

  const toggleControlCenter = () => {
    const activeElement = document.activeElement as HTMLElement;
    if (activeElement && activeElement !== document.body) activeElement.blur();

    setControlCenterOpen(!controlCenterOpen);
  };

  // Close the control center on route change
  useEffect(() => {
    setControlCenterOpen(false);
  }, [location.pathname]);

  return {
    // State
    userMenuOpen,
    controlCenterOpen,
    isHome,
    subModules,

    // Setters
    setShowUserMenu,
    setControlCenterOpen,

    // Toggles
    toggleUserMenu,
    toggleControlCenter,

    // Utils
    navigate,
    location,
  };
};

// ASSETS

// CONSTANTS
